/**
 * CommunityPage — Phase UX-C: Deterministic Community Visibility
 *
 * Key requirements:
 * - Wait for BOTH flags AND rooms to load before rendering
 * - Filter rooms ONLY after both are ready
 * - No flicker of locked rooms when visibility flag is enabled
 * - Fail-closed: if flags fail to load, use safe defaults (all false)
 *
 * PHI-Zero: no user-identifiable data logged.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { discussionsAPI, preferencesAPI } from '../api';
import { toast } from 'sonner';
import { Users, MessageSquare, Clock, ChevronRight, Search, Plus, Lock, Eye, RefreshCw } from 'lucide-react';
import type { SpecialtyRoom, DiscussionThread } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import VerificationCallout from '../components/VerificationCallout';
import { SharedDigestFilterBar, type DigestProfileStat } from '../components/shared/SharedDigestFilterBar';

// ============================================================================
// LOADING SKELETON
// ============================================================================
const RoomsSkeleton: React.FC = () => (
  <div className="space-y-2" data-testid="rooms-skeleton">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="p-3 rounded-lg bg-gray-100 animate-pulse">
        <div className="h-4 w-3/4 bg-gray-200 rounded mb-2" />
        <div className="h-3 w-1/2 bg-gray-200 rounded" />
      </div>
    ))}
  </div>
);

const FullPageSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gray-50 py-8" data-testid="community-loading">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse mb-2" />
        <div className="h-4 w-96 bg-gray-200 rounded animate-pulse" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1">
          <Card>
            <CardHeader className="pb-3">
              <div className="h-6 w-32 bg-gray-200 rounded animate-pulse" />
            </CardHeader>
            <CardContent>
              <RoomsSkeleton />
            </CardContent>
          </Card>
        </div>
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <div className="h-16 w-16 bg-gray-200 rounded-full mx-auto mb-4 animate-pulse" />
                <div className="h-6 w-48 bg-gray-200 rounded mx-auto mb-2 animate-pulse" />
                <div className="h-4 w-72 bg-gray-200 rounded mx-auto animate-pulse" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  </div>
);

// ============================================================================
// ERROR STATE
// ============================================================================
interface ErrorStateProps {
  onRetry: () => void;
  message?: string;
}

const ErrorState: React.FC<ErrorStateProps> = ({ onRetry, message }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="community-error">
    <Card className="max-w-md">
      <CardContent className="py-8 text-center">
        <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <RefreshCw className="h-8 w-8 text-red-500" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load Community</h2>
        <p className="text-gray-600 mb-4">{message || 'Unable to load community rooms. Please try again.'}</p>
        <Button onClick={onRetry}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </CardContent>
    </Card>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const CommunityPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user?.capabilities?.community_write !== false;
  
  // Phase UX-C: Feature flags with ready state
  const { flags, ready: flagsReady, error: flagsError } = useFeatureFlags();
  
  // Rooms state
  const [rooms, setRooms] = useState<SpecialtyRoom[]>([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [roomsError, setRoomsError] = useState<Error | null>(null);
  
  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState<string | null>(null);
  const [selectedSubspecialty, setSelectedSubspecialty] = useState<string | null>(null);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [digestProfiles, setDigestProfiles] = useState<DigestProfileStat[]>([]);
  const [profileSpecialtyMap, setProfileSpecialtyMap] = useState<Record<string, string>>({});
  const [threads, setThreads] = useState<DiscussionThread[]>([]);
  const [loadingThreads, setLoadingThreads] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const stats = await preferencesAPI.getProfilesScreenStats();
        const profiles = stats.profiles || [];
        setDigestProfiles(profiles);
        const map: Record<string, string> = {};
        for (const p of profiles) {
          map[p.profile_id] = p.specialty_id || p.profile_id;
        }
        setProfileSpecialtyMap(map);
      } catch {
        // Digest bar still renders; filtering falls back to profile id
      }
    })();
  }, []);

  const userDigestSpecialtyIds = useMemo(() => {
    const ids = new Set<string>();
    for (const profile of digestProfiles) {
      if (profile.specialty_id) ids.add(profile.specialty_id);
      ids.add(profile.profile_id);
    }
    return ids;
  }, [digestProfiles]);

  const effectiveSpecialtyId = useMemo(() => {
    if (!selectedProfileId) return null;
    if (rooms.some((r) => r.specialty_id === selectedProfileId)) return selectedProfileId;
    return profileSpecialtyMap[selectedProfileId] || selectedProfileId;
  }, [selectedProfileId, profileSpecialtyMap, rooms]);

  const handleProfileSelect = useCallback((id: string | null) => {
    setSelectedProfileId(id);
    if (!id) return;
    const specialtyId =
      rooms.find((r) => r.specialty_id === id)?.specialty_id ||
      profileSpecialtyMap[id] ||
      id;
    setSelectedSpecialty(specialtyId);
  }, [rooms, profileSpecialtyMap]);

  const digestFilterBar = (
    <SharedDigestFilterBar
      selectedProfileId={selectedProfileId}
      onSelectProfile={handleProfileSelect}
      showUnscreenedBadge={false}
      showSavedPercent={true}
      showPrimaryStar={true}
      showRunDigest={false}
      centered={true}
      allLabel="All Digests"
    />
  );

  // Load rooms only after flags are ready
  const loadRooms = useCallback(async () => {
    setRoomsLoading(true);
    setRoomsError(null);
    try {
      const data = await discussionsAPI.getSpecialtyRooms();
      setRooms(data.rooms);
    } catch (err) {
      setRoomsError(err instanceof Error ? err : new Error('Failed to load rooms'));
      toast.error('Failed to load community rooms');
    } finally {
      setRoomsLoading(false);
    }
  }, []);

  // Effect: Load rooms only after flags are ready
  useEffect(() => {
    if (flagsReady) {
      loadRooms();
    }
  }, [flagsReady, loadRooms]);

  // Load threads for selected specialty
  useEffect(() => {
    if (selectedSpecialty) {
      setLoadingThreads(true);
      discussionsAPI.getSpecialtyThreads(selectedSpecialty)
        .then((data) => setThreads(data.threads))
        .catch(() => toast.error('Failed to load discussions'))
        .finally(() => setLoadingThreads(false));
    }
  }, [selectedSpecialty]);

  // ============================================================================
  // DERIVED STATE (useMemo) - CRITICAL: only compute after both ready
  // ============================================================================
  
  // Filter rooms - compute flag values INLINE to avoid stale closures
  const displayedRooms = useMemo(() => {
    // Don't compute until both are ready
    if (!flagsReady || roomsLoading) return [];
    
    // Compute visibility flag value INLINE (not from another useMemo)
    const shouldHideLocked = 
      flags.enable_community_v2 === true && 
      flags.enable_community_visible_only_eligible === true;
    
    // DEBUG: Log what we're computing
    // eslint-disable-next-line no-console
    console.log('[CommunityPage] FILTER DEBUG:', {
      flagsReady,
      roomsLoading,
      enable_community_v2: flags.enable_community_v2,
      enable_community_visible_only_eligible: flags.enable_community_visible_only_eligible,
      shouldHideLocked,
      roomsCount: rooms.length,
    });
    
    let filtered = rooms;
    
    // Phase UX-C: When both flags are ON, hide locked rooms entirely
    if (shouldHideLocked) {
      filtered = filtered.filter(room => room.can_enter !== false);
      // eslint-disable-next-line no-console
      console.log('[CommunityPage] After filtering:', filtered.length, 'rooms');
    }
    
    // Filter by selected digest profile
    if (effectiveSpecialtyId) {
      filtered = filtered.filter((room) => room.specialty_id === effectiveSpecialtyId);
    } else if (userDigestSpecialtyIds.size > 0) {
      filtered = filtered.filter((room) => userDigestSpecialtyIds.has(room.specialty_id));
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(room =>
        room.specialty_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  }, [flagsReady, roomsLoading, rooms, flags.enable_community_v2, flags.enable_community_visible_only_eligible, searchTerm, effectiveSpecialtyId, userDigestSpecialtyIds]);

  // Derived flags for UI rendering (after rooms are filtered)
  const visibleOnlyEligible = flagsReady && 
    flags.enable_community_v2 === true && 
    flags.enable_community_visible_only_eligible === true;

  const subspecialtySelection = flagsReady && 
    flags.enable_community_subspecialty_selection === true;

  // Current room details
  const currentRoom = useMemo(() => {
    return rooms.find(r => r.specialty_id === selectedSpecialty);
  }, [rooms, selectedSpecialty]);

  const visibleSubspecialties = currentRoom?.visible_subspecialties || [];
  const isRoomFrozen = (currentRoom as any)?.access_state === 'frozen';
  const canPostInRoom = currentRoom?.can_post !== false && !isRoomFrozen;

  // ============================================================================
  // HELPERS
  // ============================================================================
  const formatTimeAgo = (dateString: string | null | undefined): string => {
    if (!dateString) return 'No activity';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // ============================================================================
  // RENDER STATES
  // ============================================================================
  
  // Show skeleton while loading (flags or rooms)
  if (!flagsReady || roomsLoading) {
    return (
      <>
        {digestFilterBar}
        <FullPageSkeleton />
      </>
    );
  }

  // Show error state if rooms failed to load (flags error is handled gracefully)
  if (roomsError) {
    return (
      <>
        {digestFilterBar}
        <ErrorState onRetry={loadRooms} message={roomsError.message} />
      </>
    );
  }

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50/50 via-slate-50 to-white py-8" data-testid="community-page">
      {digestFilterBar}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Community</h1>
          <p className="text-gray-600 mt-2">
            Connect with colleagues in your specialty. Share insights, discuss articles, and learn together.
          </p>
          <p className="text-sm text-amber-600 mt-2 bg-amber-50 px-3 py-2 rounded-md inline-block">
            Reminder: Do not share patient identifiers in discussions.
          </p>
          {/* DEBUG: Show flag states */}
          <p className="text-xs text-gray-400 mt-1" data-testid="debug-flags">
            DEBUG: v2={String(flags.enable_community_v2)} visible={String(flags.enable_community_visible_only_eligible)} ready={String(flagsReady)}
          </p>
          {/* Debug info for flags error (fail-closed to defaults) */}
          {flagsError && (
            <p className="text-xs text-gray-400 mt-1">
              Using default settings (feature flags unavailable)
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Specialty Rooms List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  Specialty Rooms
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search specialties..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="specialty-search"
                  />
                </div>

                {/* Rooms List */}
                <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="rooms-list">
                  {displayedRooms.map((room) => {
                    // When visibleOnlyEligible is ON, locked rooms are already filtered out
                    // When OFF, show them greyed with lock icon
                    const isLocked = room.can_enter === false;
                    const isFrozen = (room as any).access_state === 'frozen';
                    return (
                      <button
                        key={room.specialty_id}
                        onClick={() => !isLocked && setSelectedSpecialty(room.specialty_id)}
                        disabled={isLocked}
                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                          isLocked
                            ? 'bg-gray-100 cursor-not-allowed opacity-60'
                            : isFrozen
                            ? selectedSpecialty === room.specialty_id
                              ? 'bg-amber-50 border border-amber-200'
                              : 'hover:bg-amber-50/50 border border-amber-100'
                            : selectedSpecialty === room.specialty_id
                            ? 'bg-blue-50 border border-blue-200'
                            : 'hover:bg-gray-50 border border-transparent'
                        }`}
                        data-testid={`room-${room.specialty_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 text-sm">
                              {room.specialty_name}
                            </span>
                            {isFrozen && (
                              <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                                Read-only
                              </Badge>
                            )}
                          </div>
                          {isLocked && <Lock className="h-4 w-4 text-gray-400" />}
                          {isFrozen && <Eye className="h-4 w-4 text-amber-500" />}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <MessageSquare className="h-3 w-3" />
                            {room.thread_count}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {room.member_count}
                          </span>
                          {room.last_activity && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatTimeAgo(room.last_activity)}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                  {displayedRooms.length === 0 && (
                    <div className="text-center py-8 text-gray-500 text-sm" data-testid="no-rooms-message">
                      {visibleOnlyEligible 
                        ? 'No communities available. Create a digest in a specialty to join its community.'
                        : searchTerm
                        ? 'No communities match your search.'
                        : 'No communities found.'}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Thread List or Welcome */}
          <div className="lg:col-span-2">
            {selectedSpecialty ? (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      {currentRoom?.specialty_name || 'Discussions'}
                    </CardTitle>
                    {canWrite && canPostInRoom && (
                      <Button
                        onClick={() => navigate(`/community/new?specialty=${selectedSpecialty}`)}
                        size="sm"
                        data-testid="new-thread-btn"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        New Discussion
                      </Button>
                    )}
                  </div>
                  
                  {/* Phase UX-C: Subspecialty chips */}
                  {subspecialtySelection && visibleSubspecialties.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3" data-testid="subspecialty-chips">
                      <button
                        onClick={() => setSelectedSubspecialty(null)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          !selectedSubspecialty
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        data-testid="subspecialty-all"
                      >
                        All
                      </button>
                      {visibleSubspecialties.map((sub: { id: string; label: string }) => (
                        <button
                          key={sub.id}
                          onClick={() => setSelectedSubspecialty(sub.id)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            selectedSubspecialty === sub.id
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          data-testid={`subspecialty-${sub.id}`}
                        >
                          {sub.label}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Read-only notice for free users */}
                  {!canPostInRoom && (
                    <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-amber-50 text-amber-800 rounded-lg text-sm" data-testid="read-only-notice">
                      <Eye className="h-4 w-4" />
                      <span>You can read discussions. Upgrade to Pro to post.</span>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {loadingThreads ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                  ) : threads.length > 0 ? (
                    <div className="space-y-3">
                      {threads.map((thread) => (
                        <button
                          key={thread.thread_id}
                          onClick={() => navigate(`/community/thread/${thread.thread_id}`)}
                          className="w-full text-left p-4 rounded-lg border hover:border-blue-200 hover:bg-blue-50 transition-colors"
                          data-testid={`thread-${thread.thread_id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                {thread.is_pinned && (
                                  <Badge variant="secondary" className="text-xs">Pinned</Badge>
                                )}
                                <h3 className="responsive-title font-medium text-gray-900">
                                  {thread.title}
                                </h3>
                              </div>
                              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                {thread.preview_comment || 'No comments yet'}
                              </p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  By {thread.creator_name || 'Anonymous'}
                                  {thread.creator_is_verified && (
                                    <Badge variant="secondary" className="h-4 px-1 text-[9px] font-medium bg-emerald-100 text-emerald-700 border-0 ml-1">
                                      Verified
                                    </Badge>
                                  )}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MessageSquare className="h-3 w-3" />
                                  {thread.comment_count} comments
                                </span>
                                <span>{formatTimeAgo(thread.last_activity_at)}</span>
                              </div>
                            </div>
                            <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900">No discussions yet</h3>
                      <p className="text-gray-500 mt-1">Be the first to start a conversation!</p>
                      {canWrite && canPostInRoom && (
                        <Button
                          onClick={() => navigate(`/community/new?specialty=${selectedSpecialty}`)}
                          className="mt-4"
                        >
                          Start Discussion
                        </Button>
                      )}
                    </div>
                  )}
                  {/* Verification callout for unverified users */}
                  {!canWrite && (
                    <div className="mt-4">
                      <VerificationCallout />
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center">
                    <Users className="h-16 w-16 text-blue-100 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900">Welcome to the Community</h2>
                    <p className="text-gray-600 mt-2 max-w-md mx-auto">
                      Select a specialty room on the left to view and participate in discussions with your colleagues.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityPage;
